import { discardDraft, keepFiles, keptFiles, readDraft, saveDraft } from "@/shared/lib/drafts";
import type { DraftKind } from "@/shared/hooks/useDraft";
import type { Attachment, Mention } from "@/shared/types";

/* A chat composer's draft (`@/shared/lib/drafts`), one per conversation: an agent's new chat
   keeps its own until its first message names the conversation. ChatPane owns it
   (`hooks/useChatDraft`); the Composer shows and edits its words and chips. */

export const chatDraftKey = (agentId: string, chatId?: string | null) => `chat:${agentId}:${chatId ?? "new"}`;

export interface ChatDraft {
  text: string;
  mentions: Mention[];
  quote: string | null;
  /** Picked files: in memory, not storage. */
  files: Attachment[];
}

const EMPTY: ChatDraft = { text: "", mentions: [], quote: null, files: [] };

export function readChatDraft(key: string): ChatDraft {
  const saved = readDraft<ChatDraft>(key);
  return {
    text: typeof saved.text === "string" ? saved.text : "",
    mentions: Array.isArray(saved.mentions) ? saved.mentions : [],
    quote: typeof saved.quote === "string" ? saved.quote : null,
    files: keptFiles<Attachment>(key),
  };
}

export function saveChatDraft(key: string, { files, ...words }: ChatDraft): void {
  saveDraft(key, words);
  keepFiles(key, files);
}

export const CHAT_DRAFT: DraftKind<ChatDraft> = {
  empty: EMPTY,
  read: readChatDraft,
  write: saveChatDraft,
  discard: discardDraft,
  merge: (refused, now) => ({
    text: [refused.text, now.text].filter(Boolean).join("\n"),
    mentions: [...refused.mentions, ...now.mentions.filter((m) => !refused.mentions.some((r) => r.id === m.id))],
    quote: now.quote ?? refused.quote,
    files: [...refused.files, ...now.files.filter((file) => !refused.files.includes(file))],
  }),
};
